import React, { useEffect, useState } from 'react';
import { Mail, MapPin, Send, Phone, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTexts } from '../context/TextsContext';
import { getContactConfig, submitContact } from '../services/cms';

const Contact = () => {
  const { t, bool } = useTexts();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { ok:boolean, msg:string }
  const [configured, setConfigured] = useState(null); // null=loading

  useEffect(() => { getContactConfig().then((c) => setConfigured(!!c.configured)).catch(() => setConfigured(false)); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setStatus(null);
    setSending(true);
    try {
      const res = await submitContact(form);
      if (res.status === 200 && res.data?.ok === true) {
        setStatus({ ok: true, msg: t('contact_success', 'Merci, votre message a bien été envoyé.') });
        setForm({ name: '', email: '', subject: '', message: '', website: '' }); // succès uniquement → on efface
      } else {
        setStatus({ ok: false, msg: t('contact_error', 'Votre message n’a pas pu être envoyé.') });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'not_configured' || err?.response?.status === 409 || err?.response?.status === 503) {
        setStatus({ ok: false, msg: t('contact_unavailable', 'Le formulaire de contact est en cours de configuration.') });
        setConfigured(false);
      } else {
        setStatus({ ok: false, msg: t('contact_error', 'Votre message n’a pas pu être envoyé. Réessayez plus tard ou contactez-nous directement.') });
      }
      // On ne vide JAMAIS le formulaire en cas d'échec.
    } finally {
      setSending(false);
    }
  };

  const phoneOn = bool('contact_phone_enabled', true);
  const disabled = configured === false;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="contact-page">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <h1 className="font-display font-700 text-4xl text-foreground mb-4">{t('contact_title', 'Contactez Citytoon')}</h1>
          <p className="text-foreground/75 text-lg mb-8">{t('contact_intro', 'Une équipe, des idées, des projets. Écrivez-nous.')}</p>
          <div className="space-y-4">
            <a href={`mailto:${t('contact_email', 'contact@citytoon.org')}`} data-testid="contact-email" className="flex items-center gap-4 p-4 rounded-2xl bg-muted hover:bg-secondary/20 transition-colors"><div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center"><Mail className="w-5 h-5 text-white" /></div><div><p className="text-xs font-800 uppercase text-muted-foreground">Email</p><p className="font-700">{t('contact_email', 'contact@citytoon.org')}</p></div></a>
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-muted"><div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></div><div><p className="text-xs font-800 uppercase text-muted-foreground">Adresse</p><p className="font-700">{t('contact_address', 'La Rochelle, France')}</p></div></div>
            {phoneOn && t('contact_phone_display', '') && (
              <a href={t('contact_phone_link', 'tel:+33686654970')} data-testid="contact-phone" className="flex items-center gap-4 p-4 rounded-2xl bg-muted hover:bg-secondary/20 transition-colors"><div className="w-11 h-11 rounded-full bg-secondary flex items-center justify-center"><Phone className="w-5 h-5 text-secondary-foreground" /></div><div><p className="text-xs font-800 uppercase text-muted-foreground">Téléphone</p><p className="font-700">{t('contact_phone_display', '+33 6 86 65 49 70')}</p></div></a>
            )}
          </div>
        </div>

        <div>
          {disabled && (
            <div data-testid="contact-unavailable" className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-amber-800 font-600 text-sm">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" /> {t('contact_unavailable', 'Le formulaire de contact est en cours de configuration.')}
            </div>
          )}
          <form onSubmit={submit} className="bg-card border border-border rounded-3xl p-7 space-y-4 shadow-lg" noValidate>
            {/* Honeypot anti-spam (caché) */}
            <input type="text" name="website" value={form.website} onChange={upd('website')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="c-name" className="text-sm font-800 mb-1.5 block">{t('contact_label_name', 'Nom')}</label>
                <input id="c-name" required disabled={disabled} value={form.name} onChange={upd('name')} data-testid="contact-name-input" className="w-full px-4 py-3 rounded-xl bg-muted outline-none focus:ring-2 focus:ring-primary/40 font-600 disabled:opacity-50" placeholder="Votre nom" />
              </div>
              <div>
                <label htmlFor="c-email" className="text-sm font-800 mb-1.5 block">{t('contact_label_email', 'Email')}</label>
                <input id="c-email" required type="email" disabled={disabled} value={form.email} onChange={upd('email')} data-testid="contact-email-input" className="w-full px-4 py-3 rounded-xl bg-muted outline-none focus:ring-2 focus:ring-primary/40 font-600 disabled:opacity-50" placeholder="vous@email.com" />
              </div>
            </div>
            <div>
              <label htmlFor="c-subject" className="text-sm font-800 mb-1.5 block">{t('contact_label_subject', 'Sujet')}</label>
              <input id="c-subject" required disabled={disabled} value={form.subject} onChange={upd('subject')} data-testid="contact-subject-input" className="w-full px-4 py-3 rounded-xl bg-muted outline-none focus:ring-2 focus:ring-primary/40 font-600 disabled:opacity-50" placeholder="Objet de votre message" />
            </div>
            <div>
              <label htmlFor="c-message" className="text-sm font-800 mb-1.5 block">{t('contact_label_message', 'Message')}</label>
              <textarea id="c-message" required rows={5} disabled={disabled} value={form.message} onChange={upd('message')} data-testid="contact-message-input" className="w-full px-4 py-3 rounded-xl bg-muted outline-none focus:ring-2 focus:ring-primary/40 font-600 resize-none disabled:opacity-50" placeholder="Votre message..." />
            </div>

            {status && (
              <div role={status.ok ? 'status' : 'alert'} data-testid={status.ok ? 'contact-success' : 'contact-error'}
                className={`flex items-start gap-2 rounded-xl p-3 text-sm font-700 ${status.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.ok ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertTriangle className="w-5 h-5 shrink-0" />} {status.msg}
              </div>
            )}

            <button type="submit" disabled={sending || disabled} data-testid="contact-submit-btn" className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-800 hover:scale-[1.02] transition-transform shadow-lg shadow-primary/25 disabled:opacity-60 disabled:hover:scale-100">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} {sending ? 'Envoi…' : t('contact_submit_label', 'Envoyer le message')}
            </button>
            {t('contact_privacy', '') && <p className="text-xs text-muted-foreground text-center">{t('contact_privacy', '')}</p>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
